<template>
<div
  ref='activity-overlay'
  class='assessment'
  :class='main_class'
>
  <button
    ref='close-button'
    class='assessment_close'
    :style='close_button_style'
    @click.prevent='stop'
  >
    {{ $t('close') }}
  </button>

  <!-- Results Box -->
  <Result
    v-if='show_results'
    :close='toggle'
    :result='result'
    :slide_result='slide_result'
    :activity='activity'
  />

  <!-- Header-->
  <div
    ref='header'
    class='assessment_header'
  >
    <div class='assessment_header-label body-four'>
      {{ label }}
    </div>
    <div class='assessment_header-title h-one'>
      {{ activity.headline }}
    </div>
  </div>

  <div class='assessment_content'>
    <!-- Preloader -->
    <div
      v-if='!is_data_loaded'
      class='assessment_loading'
    >
      Loading
    </div>

    <!-- Section Start -->
    <template v-if='is_data_loaded'>
    <template
      v-for='(section, section_index) in sections'
      :key='section_index'
    >
      <div
        v-if="section.introduction"
        class='assessment_section'
      >
        <div class='h-four'>
          {{ section.introduction.headline }}
        </div>

        <div
          class='assessment_section-content'
          v-html='section.introduction.content'
        />
      </div>

      <div class='assessment_questions'>
        <!-- Question -->
        <Question
          v-for='(question, q_index) in section.questions'
          :key='q_index'
          :index='q_index'
          :section='section'
          :question='question'
          :select='select'
          :isOptionSelected='isOptionSelected'
          :isQuestionCorrect='isQuestionCorrect'
          :show_result='show_results'
          :disabled='show_results'
        />
        <!-- Question End -->
      </div>
    </template>
    </template>
    <!-- Section End -->

    <!-- Submit -->
    <div
      v-if='is_data_loaded && !show_results'
      class='assessment_submit'
    >
      <button
        class='button-one-branded'
        :disabled='!submit_enabled'
        @click.prevent='submit'
      >
        {{ $t('submit') }}
      </button>

      <div
        v-if='!submit_enabled && questions_to_answer'
        class='assessment_submit-error'
      >
        <div class='assessment_submit-error-inner'>
          {{ $t('you_must_answer_questions', { value: questions_to_answer }) }}
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import Question from './assessment-v1-question'
import Result from './assessment-v1-result'

// import OverlayAccessibilityMixin from "../../overlay-accessibility-mixin"

export default {
  // mixins: [OverlayAccessibilityMixin],

  props: {
    activity: Object,
    slide: Object,
    api_client: Object,
    slide_result: Object,
    assessment_results_opened: Boolean,
    toggle: Function,
    updateResult: Function,
    updateCurrentActivity: Function
  },

  data() {
    return {
      local_activity_results: {},
      is_data_loaded: false,
      current_attempt: null,
      sections: null,
      header_offset: null
    }
  },

  mounted() {
    // fetch data
    if (!this.show_results) {
      this.fetchData()
    } else {
      // if we show results then we just get data from previous_attempt
      this.is_data_loaded = true
      this.sections = this.result.sections
    }

    this.closeAction = this.stop

    this.calculateHeaderOffset()

    window.addEventListener('resize', this.calculateHeaderOffset)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.calculateHeaderOffset)
  },

  components: {
    Question,
    Result
  },

  computed: {
    answered_length() {
      let answered_length = 0
      this.local_activity_results.sections.forEach((s) => {
        s.questions.forEach((q) => {
          if (q.selection.length) answered_length += 1
        })
      })

      return answered_length
    },

    submit_enabled() {
      return this.answered_length === this.total_questions
    },

    hasAnswers() {
      return this.answered_length > 0
    },

    label() {
      if (this.show_results) {
        if (this.activity.multiple_attempts) {
          const attempt = this.slide_result.results.attempts ? this.slide_result.results.attempts.filter((a) => a.passed !== null).length : 1
          return this.$t('attempt') + ' ' + attempt + ' ' + this.$t('results')
        } else {
          return this.$t('results')
        }
      }

      return this.activity.label || this.$t('assessment')
    },

    show_results() {
      return this.assessment_results_opened
    },

    result() {
      return this.activity.previous_attempt
    },

    main_class() {
      return this.show_results ? 'assessment--' + (this.result.passed ? 'passed' : 'failed') : ''
    },

    total_questions() {
      let total = 0
      this.sections.forEach((s) => {
        total += s.questions.length
      })

      return total
    },

    questions_to_answer() {
      const unanswered = []; let question_index = 0
      this.local_activity_results.sections.forEach((s, s_index) => {
        s.questions.forEach((q, q_index) => {
          question_index += 1
          if (!q.selection.length) {
            unanswered.push({
              index: question_index,
              id: q.id
            })
          }
        })
      })

      if (unanswered.length) {
        return unanswered.map((q) => { return q.index }).join(', ')
      } else return false
    },

    close_button_style() {
      if (!this.show_results) return false
      const padding = window.innerWidth > 740 ? 30 : 24
      return `top: ${this.header_offset + padding}px`
    }
  },

  methods: {
    fetchData() {
      const attempt_url = this.api_client.resourceUrlFor('activity', this.activity.id) + '/attempt'
      this.api_client.request(attempt_url, 'GET', { slide_id: this.slide.id })
        .then(({ data }) => {
          this.current_attempt = data.current_attempt
          this.sections = this.current_attempt.sections
          this.is_data_loaded = true
          this.buildResults()
        })
    },

    buildResults() {
      // Build local_activity_results structure
      const results = { id: this.current_attempt.id, sections: [] }

      this.sections.forEach((s) => {
        const questions = []
        s.questions.forEach((q) => {
          questions.push({
            id: q.id,
            selection: []
          })
        })

        results.sections.push({
          id: s.id,
          questions
        })
      })

      this.local_activity_results = results
    },

    stop() {
      const closeOverlay = !this.show_results && this.hasAnswers ? confirm(this.$t('confirm_close_knowledge_check')) : true

      if (closeOverlay) {
        this.toggle()
      }
    },

    select(section, question, option) {
      if (this.show_results) return false

      let section_index = 0
      this.local_activity_results.sections.forEach((s, i) => { if (s.id === section.id) { section_index = i  } })

      let question_index = 0
      this.local_activity_results.sections[section_index].questions.forEach((q, i) => { if (q.id === question.id) { question_index = i  } })

      const selection = this.local_activity_results.sections[section_index].questions[question_index].selection

      // Push options Multiple Choice
      if (question.kind === 'multiple_choice') {
        this.local_activity_results.sections[section_index].questions[question_index].selection = [option.id]
      }

      // Push options Multiple Select
      if (question.kind === 'multiple_select') {
        const index = selection.indexOf(option.id)
        if (index === -1)  {
          selection.push(option.id)
        } else {
          selection.splice(index, 1)
        }

        if (selection.length) {
          this.local_activity_results.sections[section_index].questions[question_index].selection = selection
        }
      }
    },

    isOptionSelected(section_id, question_id, option_id) {
      // Find option in sections array of previous_attempt object (show results)
      if (this.show_results) {
        let selected = false
        this.sections.forEach((s) => {
          s.questions.forEach((q) => {
            q.options.forEach((o) => {
              if (option_id === o.id && o.is_selected) selected = true
            })
          })
        })
        return selected
      } else {
        // Find option in local_activity_results
        let section_index = 0
        this.local_activity_results.sections.forEach((s, i) => { if (s.id === section_id) { section_index = i  } })

        let question_index = 0
        this.local_activity_results.sections[section_index].questions.forEach((q, i) => { if (q.id === question_id) { question_index = i  } })

        if (this.local_activity_results.sections[section_index].questions[question_index].selection.indexOf(option_id) !== -1) return true
        return false
      }
    },

    isQuestionCorrect(question_id) {
      let is_correct = false
      this.result.sections.forEach((s) => {
        s.questions.forEach((q) => {
          if (q.id === question_id && q.is_correct) {
            is_correct = true
          }
        })
      })
      return is_correct
    },

    submit() {
      // Show preloader and
      this.is_data_loaded = false

      // scroll page to top of the results
      this.$scrollTo(this.$refs.header, {
        container: this.$el,
        duration: 500,
        offset: 0
      })

      this.updateResult('finished', this.local_activity_results, null, {
        include: 'slide.activity',
        callback: (data) => {
          this.updateCurrentActivity(data.activity)
          this.toggle({ showAssessmentResults: true })
          this.sections = data.activity.previous_attempt.sections
          this.is_data_loaded = true
        }
      })
    },

    calculateHeaderOffset() {
      if (!this.show_results) return false
      this.header_offset = this.$refs.header.offsetTop
    }
  }
}
</script>

<style lang='sass' scoped>
@import './overlay'
</style>
